import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TaskItem, TaskList } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import {
  FlatRow,
  LIST_SHAPE,
  MAX_DEPTH,
  PROJECT_MAX_DEPTH,
  TreeShape,
  blockEnd,
  canIndent,
  childrenOf,
  flattenRoots,
  indent,
  moveBlock,
  outdent,
  toMoves,
} from '../../utils/taskTree';
import { projectItemUrl } from '../../utils/taskLinks';
import { blobToBase64, MAX_RAW_BYTES, prepareImage } from '../../utils/imagePrep';
import { userName } from '../../utils/taskUsers';
import ItemRow from './ItemRow';
import Popover from './Popover';
import TaskIcon from './TaskIcon';
import { UserBubble } from './UserBubble';

// Keep-style card tints. null = plain surface.
export const LIST_COLORS: { value: string | null; label: string }[] = [
  { value: null, label: 'Default' },
  { value: '#FBE9E7', label: 'Coral' },
  { value: '#FFF3E0', label: 'Peach' },
  { value: '#FFF8D6', label: 'Sand' },
  { value: '#E8F5EC', label: 'Mint' },
  { value: '#E6F4F4', label: 'Sage' },
  { value: '#E8F0FC', label: 'Sky' },
  { value: '#F0EBFA', label: 'Lavender' },
  { value: '#F3F1EC', label: 'Stone' },
];

interface ListCardProps {
  list: TaskList;
  items: TaskItem[];
  members: string[];
  readOnly?: boolean;
  /** The MPP week card: full width, always first. */
  featured?: boolean;
  /** Pinned by me (shown after the week list). */
  isPinned?: boolean;
  onTogglePin?: (list: TaskList) => void;
  highlightIds: Set<number>;
  flashId: number | null;
  mineOnly: boolean;
  search: string;
  autoFocusTitle?: boolean;
  onOpenImage: (url: string) => void;
  onShare: (list: TaskList) => void;
  onDelete: (list: TaskList) => void;
  /**
   * A project branch on Lists (a list of kind 'view'): the project whose items
   * these are, and the node the card starts from (null = the whole project).
   */
  source?: { project: TaskList; root: TaskItem | null; filter?: 'branch' | 'materials' };
  /** How the rows sit in their own list (see TreeShape). Defaults to a plain list. */
  shape?: TreeShape;
  /** No header or progress bar: the project page shows its own. */
  bare?: boolean;
  /** One flat list, no nesting or reordering (a project's shopping list on Lists). */
  flat?: boolean;
  /** What new lines are, when not plain tasks (materials on a shopping list). */
  newItemKind?: TaskItem['node_kind'];
  /** Folding (a project's Outline): branches folded away, and how to fold one. */
  collapsed?: Set<number>;
  onToggleFold?: (item: TaskItem) => void;
}

const keyOf = (item: TaskItem) => item.ckey || String(item.id);

interface DragState {
  key: string;
  from: number;
  base: FlatRow[];
  preview: FlatRow[];
}

const ListCard: React.FC<ListCardProps> = ({
  list,
  items,
  members,
  readOnly = false,
  featured = false,
  isPinned = false,
  onTogglePin,
  highlightIds,
  flashId,
  mineOnly,
  search,
  autoFocusTitle,
  onOpenImage,
  onShare,
  onDelete,
  source,
  shape = LIST_SHAPE,
  bare = false,
  flat = false,
  newItemKind,
  collapsed,
  onToggleFold,
}) => {
  const { me, today, users, weeklyViewer, createItem, updateItem, deleteItem, deleteList, moveItems, updateList, uploadImage, deleteImage } = useTasks();
  const navigate = useNavigate();
  const create: typeof createItem = (input) => createItem(newItemKind ? { ...input, node_kind: newItemKind } : input);
  // A week list I only see for the lines assigned to me: I can work those
  // lines, but not add to, reorder or restyle the list.
  const limited = list.is_team && !weeklyViewer && !readOnly;
  // A project's card edits the project: new lines and moves go to its list.
  const itemListId = source ? source.project.id : list.id;
  const realMaxDepth = source || list.kind === 'project' ? PROJECT_MAX_DEPTH : MAX_DEPTH;
  const shoppingList = source?.filter === 'materials';
  const shownTitle = source
    ? `${source.root ? source.root.text : source.project.title}${shoppingList ? ' — to buy' : ''}`
    : list.title;
  const [title, setTitle] = useState(shownTitle);
  const [newText, setNewText] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [showWaiting, setShowWaiting] = useState(true);
  const [focus, setFocus] = useState<{ key: string; caret: 'start' | 'end' } | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const rowEls = useRef(new Map<string, HTMLLIElement>());
  const menuRef = useRef<HTMLButtonElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const titleFocused = useRef(false);
  useEffect(() => {
    if (!titleFocused.current) setTitle(shownTitle);
  }, [shownTitle]);

  // ── Shape the tree into sections ───────────────────────────────────────────
  const kids = useMemo(() => childrenOf(items), [items]);
  const query = search.trim().toLowerCase();
  // A search matching the list's own title shows the whole list.
  const titleMatch = !!query && (list.title || '').toLowerCase().includes(query);
  const itemQuery = titleMatch ? '' : query;
  const filtered = mineOnly || !!itemQuery;

  const sections = useMemo(() => {
    const roots = kids.get(shape.rootParent) || [];
    const subtree = (root: TaskItem): TaskItem[] => {
      const out = [root];
      for (const child of kids.get(root.id) || []) out.push(...subtree(child));
      return out;
    };
    const matches = (root: TaskItem) => {
      if (!filtered) return true;
      const all = subtree(root);
      if (mineOnly && !all.some((i) => i.assignees.includes(me))) return false;
      if (itemQuery && !all.some((i) => i.text.toLowerCase().includes(itemQuery) || (i.note || '').toLowerCase().includes(itemQuery))) return false;
      return true;
    };
    const isWaiting = (i: TaskItem) => !!i.waiting_on && (!i.follow_up_date || i.follow_up_date > today);
    const visible = roots.filter(matches);
    return {
      open: visible.filter((r) => r.progress < 100 && !isWaiting(r)),
      waiting: visible.filter((r) => r.progress < 100 && isWaiting(r)),
      done: visible
        .filter((r) => r.progress >= 100)
        .sort((a, b) => (b.completed_at || '').localeCompare(a.completed_at || '')),
      total: roots.length,
      doneCount: roots.filter((r) => r.progress >= 100).length,
    };
  }, [kids, filtered, mineOnly, itemQuery, me, today, shape.rootParent]);

  // A search or "Assigned to me" opens folded branches so matches can be seen.
  const folded = filtered ? undefined : collapsed;
  const baseRows = useMemo(
    () => flattenRoots(sections.open, kids, shape.maxDepth, folded),
    [sections.open, kids, shape.maxDepth, folded]
  );
  const rows = drag ? drag.preview : baseRows;
  const structural = !readOnly && !filtered && !limited && !flat;

  // ── Drag (pointer events: works with touch, survives leaving the row) ─────
  const onPointerDown = (e: React.PointerEvent, index: number) => {
    if (!structural || (e.button != null && e.button !== 0)) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    setDrag({ key: keyOf(baseRows[index].item), from: index, base: baseRows, preview: baseRows });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag) return;
    const end = blockEnd(drag.base, drag.from);
    const rest = drag.base.filter((_, i) => i < drag.from || i >= end);
    // Insertion point = how many of the other rows sit above the pointer.
    let to = 0;
    for (const row of rest) {
      const el = rowEls.current.get(keyOf(row.item));
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (e.clientY > rect.top + rect.height / 2) to++;
    }
    const preview = moveBlock(drag.base, drag.from, to, shape.maxDepth);
    const same =
      preview.length === drag.preview.length &&
      preview.every((r, i) => r.item.id === drag.preview[i].item.id && r.depth === drag.preview[i].depth);
    if (!same) setDrag({ ...drag, preview });
  };

  const onPointerUp = () => {
    if (!drag) return;
    const moves = toMoves(drag.preview, shape);
    setDrag(null);
    if (moves.length) moveItems(itemListId, moves);
  };

  const dragHandleProps = (index: number) => ({
    onPointerDown: (e: React.PointerEvent) => onPointerDown(e, index),
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
  });

  // ── Keyboard / menu structure edits ────────────────────────────────────────
  const applyRows = useCallback(
    (next: FlatRow[], keepFocus: TaskItem) => {
      const moves = toMoves(next, shape);
      if (moves.length) moveItems(itemListId, moves);
      setFocus({ key: keyOf(keepFocus), caret: 'end' });
    },
    [itemListId, moveItems, shape]
  );

  const insertAfter = (index: number) => {
    const item = rows[index].item;
    const children = kids.get(item.id) || [];
    if (children.length && rows[index].depth < shape.maxDepth) {
      // On a parent, Enter starts a new first child.
      const { key } = create({ list_id: itemListId, parent_id: item.id, text: '', sort_order: children[0].sort_order - 1 });
      setFocus({ key, caret: 'start' });
      return;
    }
    const siblings = kids.get(item.parent_id ?? null) || [];
    const at = siblings.findIndex((s) => s.id === item.id);
    const next = siblings[at + 1];
    const sortOrder = next ? (item.sort_order + next.sort_order) / 2 : item.sort_order + 1;
    const { key } = create({ list_id: itemListId, parent_id: item.parent_id, text: '', sort_order: sortOrder });
    setFocus({ key, caret: 'start' });
  };

  // A week-list link can't take sub-items (they live on the original), so
  // nothing may be indented under one.
  const indentAllowed = (flat: FlatRow[], index: number) => {
    if (!canIndent(flat, index, shape.maxDepth)) return false;
    const move = toMoves(indent(flat, index, shape.maxDepth), shape).find((m) => m.id === flat[index].item.id);
    const parent = move?.parent_id ? items.find((i) => i.id === move.parent_id) : undefined;
    return !parent?.linked_to;
  };

  const addChild = (item: TaskItem) => {
    const children = kids.get(item.id) || [];
    const last = children[children.length - 1];
    const { key } = create({ list_id: itemListId, parent_id: item.id, text: '', sort_order: last ? last.sort_order + 1 : 1 });
    setFocus({ key, caret: 'start' });
  };

  // ── List-level actions ─────────────────────────────────────────────────────
  const addTyped = () => {
    const text = newText.trim();
    if (!text) return;
    create({ list_id: itemListId, parent_id: shape.rootParent, text });
    setNewText('');
  };
  const submitNew = (e: React.FormEvent) => {
    e.preventDefault();
    addTyped();
  };

  const commitTitle = () => {
    titleFocused.current = false;
    const next = title.trim();
    if (next === shownTitle) return;
    // A project card's title is its branch (or the project) — renaming it renames that.
    if (source?.root) updateItem(source.root.id, { text: next });
    else if (source) updateList(source.project.id, { title: next });
    else updateList(list.id, { title: next });
  };

  /** Lines a project card doesn't show below its deepest level. */
  const countBelow = (id: number): number =>
    (kids.get(id) || []).reduce((n, child) => n + 1 + countBelow(child.id), 0);

  const onFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploadError(null);
    setUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) throw new Error(`"${file.name}" isn't an image.`);
        const { blob, contentType } = await prepareImage(file);
        if (blob.size > MAX_RAW_BYTES) throw new Error(`"${file.name}" is too large even after resizing.`);
        await uploadImage(list.id, { filename: file.name, contentType, data: await blobToBase64(blob) });
      }
    } catch (error: any) {
      setUploadError(error?.response?.data?.error || error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const renderRows = (flat: FlatRow[], opts: { draggable: boolean; readOnly: boolean }) =>
    flat.map((row, index) => {
      const { item } = row;
      const key = keyOf(item);
      const hasChildren = (kids.get(item.id) || []).length > 0;
      const atBottom = row.depth >= shape.maxDepth;
      return (
        <ItemRow
          key={key}
          item={item}
          depth={row.depth}
          hasChildren={hasChildren}
          readOnly={opts.readOnly}
          isNew={highlightIds.has(item.id)}
          flash={flashId === item.id}
          dragging={drag?.key === key || (!!drag && isInDragBlock(drag, item.id))}
          draggable={opts.draggable}
          dragHandleProps={opts.draggable ? dragHandleProps(index) : undefined}
          rowRef={(el) => {
            if (el) rowEls.current.set(key, el);
            else rowEls.current.delete(key);
          }}
          focusCaret={focus?.key === key ? focus.caret : null}
          onFocused={() => setFocus(null)}
          canIndent={opts.draggable && indentAllowed(flat, index)}
          canOutdent={opts.draggable && row.depth > 0}
          canAddChild={!atBottom && item.depth < realMaxDepth}
          fold={
            onToggleFold
              ? { has: hasChildren && !atBottom, collapsed: !!folded?.has(item.id), hidden: countBelow(item.id), onToggle: () => onToggleFold(item) }
              : undefined
          }
          moreBelow={
            source && atBottom && hasChildren
              ? { count: countBelow(item.id), onOpen: () => navigate(projectItemUrl(source.project.id, item.id)) }
              : undefined
          }
          onEnter={() => !opts.readOnly && !limited && insertAfterIn(flat, index)}
          onIndent={() => opts.draggable && indentAllowed(flat, index) && applyRows(indent(flat, index, shape.maxDepth), item)}
          onOutdent={() => opts.draggable && row.depth > 0 && applyRows(outdent(flat, index, shape.maxDepth), item)}
          onRemoveEmpty={() => {
            deleteItem(item.id);
            if (index > 0) setFocus({ key: keyOf(flat[index - 1].item), caret: 'end' });
          }}
          onFocusPrev={() => index > 0 && setFocus({ key: keyOf(flat[index - 1].item), caret: 'end' })}
          onFocusNext={() => index < flat.length - 1 && setFocus({ key: keyOf(flat[index + 1].item), caret: 'start' })}
          onAddChild={() => addChild(item)}
        />
      );
    });

  // Enter in the Waiting / Done sections adds a sibling in place; only the
  // open section's rows (`rows`) drive the structural insert.
  const insertAfterIn = (flat: FlatRow[], index: number) => {
    if (flat === rows) return insertAfter(index);
    const item = flat[index].item;
    const { key } = create({ list_id: itemListId, parent_id: item.parent_id, text: '', sort_order: item.sort_order + 0.5 });
    setFocus({ key, caret: 'start' });
  };

  const isWeekly = list.kind === 'weekly';
  const isView = list.kind === 'view';
  const openInProject = source
    ? () => navigate(projectItemUrl(source.project.id, source.root?.id))
    : undefined;
  const memberEmails = list.is_team ? [] : [list.owner_email, ...members.filter((m) => m !== list.owner_email)];
  const progressPct = sections.total ? Math.round((sections.doneCount / sections.total) * 100) : 0;
  const hidden = filtered && !sections.open.length && !sections.waiting.length && !sections.done.length;
  if (hidden && !featured) return null;

  return (
    <section
      className={`tl-card${featured ? ' tl-card-pinned' : ''}${readOnly ? ' tl-card-readonly' : ''}${bare ? ' tl-card-bare' : ''}`}
      style={list.color ? ({ '--tl-tint': list.color } as React.CSSProperties) : undefined}
      data-list-id={list.id}
    >
      {!bare && (
      <header className="tl-card-head">
        <div className="tl-card-title-wrap">
          {featured && <TaskIcon name="pin" size={14} className="tl-pin" title="This week's MPP list — always first" />}
          {isPinned && !featured && (
            <button type="button" className="tl-pin-btn" onClick={() => onTogglePin?.(list)} title="Pinned — click to unpin" aria-label="Unpin list">
              <TaskIcon name="pin" size={14} />
            </button>
          )}
          {isWeekly || readOnly || shoppingList ? (
            <h2 className="tl-card-title tl-card-title-static">{shownTitle || 'Untitled list'}</h2>
          ) : (
            <input
              className="tl-card-title"
              value={title}
              placeholder="Title"
              onFocus={() => (titleFocused.current = true)}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
              autoFocus={autoFocusTitle}
              aria-label="List title"
            />
          )}
          {isWeekly && !readOnly && <span className="tl-week-pill">This week</span>}
        </div>
        {source && (
          <button type="button" className="tk-chip tk-chip-week tl-from-project" onClick={openInProject} title="Open it in the project">
            <TaskIcon name="project" size={11} />
            {source.root ? source.project.title || 'Untitled project' : 'Project'}
          </button>
        )}

        <div className="tl-card-tools">
          {list.is_team ? (
            <span className="tl-team" title="Shared with everyone">Team</span>
          ) : (
            memberEmails.length > 1 && (
              <span className="tl-members" title={memberEmails.map((m) => userName(users, m)).join(', ')}>
                {memberEmails.slice(0, 4).map((m) => (
                  <UserBubble key={m} users={users} email={m} title="" />
                ))}
                {memberEmails.length > 4 && <span className="tl-members-more">+{memberEmails.length - 4}</span>}
              </span>
            )
          )}
          {!readOnly && !list.is_team && (
            <button type="button" className="tk-icon-btn" onClick={() => onShare(list)} aria-label="Share list" title="Share">
              <TaskIcon name="share" />
            </button>
          )}
          {((!readOnly && !limited) || (!isWeekly && list.archived) || (isView && list.owner_email === me)) && (
            <button ref={menuRef} type="button" className="tk-icon-btn" onClick={() => setMenuOpen(true)} aria-label="List options">
              <TaskIcon name="more" />
            </button>
          )}
        </div>
      </header>
      )}

      {!bare && sections.total > 0 && (
        <div className="tl-progress" title={`${sections.doneCount} of ${sections.total} done`}>
          <div className="tl-progress-bar"><span style={{ width: `${progressPct}%` }} /></div>
          <span className="tl-progress-text">{sections.doneCount}/{sections.total}</span>
        </div>
      )}

      <ul className={`ti-list${drag ? ' is-dragging' : ''}`}>{renderRows(rows, { draggable: structural, readOnly })}</ul>

      {!rows.length && !sections.waiting.length && !sections.done.length && (
        <p className="tl-empty">
          {isView && !source
            ? "You're no longer on the project this came from."
            : filtered ? 'Nothing here matches.' : readOnly ? 'No items.' : 'Nothing on this list yet.'}
        </p>
      )}

      {limited && <p className="tl-empty tl-limited">Only the tasks assigned to you on this list are shown.</p>}

      {!readOnly && !limited && (
        <form className="tl-add" onSubmit={submitNew}>
          <TaskIcon name="plus" size={16} className="tl-add-plus" />
          <input
            className="tl-add-input"
            value={newText}
            placeholder={list.kind === 'project' ? 'Main branch' : 'List item'}
            onChange={(e) => setNewText(e.target.value)}
            onBlur={addTyped}
            aria-label={`Add an item to ${list.title || 'this list'}`}
          />
        </form>
      )}

      {sections.waiting.length > 0 && (
        <div className="tl-section">
          <button type="button" className="tl-section-toggle" onClick={() => setShowWaiting((v) => !v)} aria-expanded={showWaiting}>
            <TaskIcon name="chevron-down" size={14} className={showWaiting ? '' : 'tl-rot'} />
            <TaskIcon name="waiting" size={13} /> Waiting on others · {sections.waiting.length}
          </button>
          {showWaiting && <ul className="ti-list ti-list-waiting">{renderRows(flattenRoots(sections.waiting, kids, shape.maxDepth, folded), { draggable: false, readOnly })}</ul>}
        </div>
      )}

      {sections.done.length > 0 && (
        <div className="tl-section">
          <button type="button" className="tl-section-toggle" onClick={() => setShowDone((v) => !v)} aria-expanded={showDone}>
            <TaskIcon name="chevron-down" size={14} className={showDone ? '' : 'tl-rot'} />
            {sections.done.length} completed item{sections.done.length === 1 ? '' : 's'}
          </button>
          {showDone && <ul className="ti-list ti-list-done">{renderRows(flattenRoots(sections.done, kids, shape.maxDepth, folded), { draggable: false, readOnly })}</ul>}
        </div>
      )}

      {list.images.length > 0 && (
        <div className="tl-images">
          {list.images.map((img) => (
            <div key={img.url} className="tl-image">
              <button type="button" className="tl-image-btn" onClick={() => onOpenImage(img.url)} aria-label="Open photo">
                <img src={img.url} alt="" loading="lazy" />
              </button>
              {!readOnly && (
                <button type="button" className="tl-image-remove" onClick={() => deleteImage(list.id, img.url)} aria-label="Remove photo">
                  <TaskIcon name="close" size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {uploading && <p className="tl-note">Uploading…</p>}
      {uploadError && <p className="tl-error">{uploadError}</p>}
      <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} />

      {menuOpen && (
        <Popover anchor={menuRef.current} onClose={() => setMenuOpen(false)} label="List options" width={250}>
          <div className="tk-menu">
            {!readOnly && (
              <>
                {!isWeekly && onTogglePin && (
                  <>
                    <button type="button" className="tk-menu-item" onClick={() => { setMenuOpen(false); onTogglePin(list); }}>
                      <TaskIcon name="pin" size={15} /> {isPinned ? 'Unpin' : 'Pin to top'}
                    </button>
                    <div className="tk-menu-sep" />
                  </>
                )}
                <div className="tk-menu-title">Colour</div>
                <div className="tl-swatches">
                  {LIST_COLORS.map((c) => (
                    <button
                      key={c.label}
                      type="button"
                      className={`tl-swatch${(list.color || null) === c.value ? ' is-selected' : ''}`}
                      style={{ background: c.value || 'var(--color-surface)' }}
                      title={c.label}
                      aria-label={c.label}
                      onClick={() => updateList(list.id, { color: c.value })}
                    />
                  ))}
                </div>
                <div className="tk-menu-sep" />
                {openInProject && (
                  <button type="button" className="tk-menu-item" onClick={() => { setMenuOpen(false); openInProject(); }}>
                    <TaskIcon name="project" size={15} /> Open in the project
                  </button>
                )}
                {!isView && (
                  <button type="button" className="tk-menu-item" onClick={() => { setMenuOpen(false); fileRef.current?.click(); }}>
                    <TaskIcon name="image" size={15} /> Add photo
                  </button>
                )}
                {!list.is_team && (
                  <button type="button" className="tk-menu-item" onClick={() => { setMenuOpen(false); onShare(list); }}>
                    <TaskIcon name="share" size={15} /> Share
                  </button>
                )}
              </>
            )}
            {isView && list.owner_email === me && (
              <button type="button" className="tk-menu-item" onClick={() => { setMenuOpen(false); deleteList(list.id); }}>
                <TaskIcon name="close" size={15} /> Remove from my Lists
              </button>
            )}
            {!isWeekly && !isView && (list.owner_email === me || users.find((u) => u.email === me)?.user_type === 'Admin') && (
              <>
                <button type="button" className="tk-menu-item" onClick={() => { setMenuOpen(false); updateList(list.id, { archived: !list.archived }); }}>
                  <TaskIcon name={list.archived ? 'unarchive' : 'archive'} size={15} /> {list.archived ? 'Restore from archive' : 'Archive'}
                </button>
                {!readOnly && (
                  <button type="button" className="tk-menu-item tk-menu-danger" onClick={() => { setMenuOpen(false); onDelete(list); }}>
                    <TaskIcon name="trash" size={15} /> Delete list
                  </button>
                )}
              </>
            )}
          </div>
        </Popover>
      )}
    </section>
  );
};

function isInDragBlock(drag: DragState, itemId: number): boolean {
  const end = blockEnd(drag.base, drag.from);
  return drag.base.slice(drag.from, end).some((r) => r.item.id === itemId);
}

export default ListCard;
